import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Button from '../../../molecules/Button';
import { getNetworkFlags } from '../../../wallet/helpers';
import NetworkLogoutWindow from './NetworkLogoutWindow';
import { LimitTabs } from '../reducers/dex/limitReducer';
import { ModalTypes } from '../../../reducers/modal';
import { actionShowModal } from '../../../actions/modal';
import { useWallet } from '../../../wallet/walletContext';
import useChangeNetwork from '../../../hooks/useChangeNetwork';

const getKeyByValue = (object: Record<string, any>, value: any) =>
  Object.keys(object).find((key) => object[key] === value);

interface ICreateButtonProps {
  limitTab: LimitTabs;
  onDeployRequested: () => any;
  createCondition: boolean;
  onCreate(): void;
  label: string;
  chainId: number;
  isCorrectChain: boolean;
  account: string | null | undefined;
}

const LimitCreateButton = ({
  limitTab,
  createCondition,
  onCreate,
  label,
  chainId,
  isCorrectChain,
  onDeployRequested,
  account,
}: ICreateButtonProps) => {
  const [isNetworkType, setIsNetworkType] = useState(false);
  const [openNetworkLogout, setOpenNetworkLogout] = useState(false);
  const dispatch = useDispatch();
  const { onDisconnect } = useWallet();
  const { networkId, changeNetwork } = useChangeNetwork();

  useEffect(() => {
    if (networkId && chainId) {
      const userChainFlag = getKeyByValue(getNetworkFlags(networkId), true);
      const tickerChainFlag = getKeyByValue(getNetworkFlags(chainId), true);

      setIsNetworkType(userChainFlag !== tickerChainFlag);
    }
  }, [networkId, chainId]);

  const onChangeChain = () => {
    if (isNetworkType) {
      return setOpenNetworkLogout(true);
    }

    if (chainId) {
      changeNetwork(chainId);
    }
  };

  /**
   * Disconnect user if he wants to switch to not supported network
   */
  const handleDisconnect = useCallback(() => {
    onDisconnect();
    setOpenNetworkLogout(false);
  }, [onDisconnect]);

  const handleConnect = useCallback(() => {
    dispatch(actionShowModal(ModalTypes.connect, {}));
  }, [dispatch]);

  switch (limitTab) {
  case LimitTabs.NotSupported:
    return (
      <Button
        action={account ? onDeployRequested : handleConnect}
        size='m'
        variant='primary'
        aria-label={
          account
            ? 'This network is not supported yet'
            : 'Connect wallet to use this feature'
        }
        text={
          account
            ? 'This network is not supported yet'
            : 'Connect wallet to use this feature'
        }
      />
    );
  case LimitTabs.NotAvailable:
    return (
      <>
        {isCorrectChain ? (
          <Button
            action={onDeployRequested}
            size='m'
            variant='primary'
            aria-label='Deploy proxy contract'
            text='Deploy proxy contract'
          />
        ) : (
          <Button
            action={onChangeChain}
            size='m'
            variant='secondary'
            aria-label='Switch network'
            text={isNetworkType ? 'Change network type' : 'Switch network'}
          />
        )}
      </>
    );
  case LimitTabs.Deploying:
    return (
      <Button
        disabled={true}
        action={() => {}}
        size='m'
        variant='primary'
        aria-label='Deploying...'
        text='Deploying...'
      />
    );
  default:
    return (
      <>
        {isCorrectChain ? (
          <Button
            disabled={createCondition}
            action={onCreate}
            size='m'
            variant='primary'
            aria-label={label}
            text={label}
          />
        ) : (
          <Button
            action={onChangeChain}
            size='m'
            variant='secondary'
            aria-label='Switch network'
            text={isNetworkType ? 'Change network type' : 'Switch network'}
          />
        )}
        {openNetworkLogout && (
          <NetworkLogoutWindow
            onClose={() => setOpenNetworkLogout(false)}
            onDisconnect={handleDisconnect}
          />
        )}
      </>
    );
  }
};

export default LimitCreateButton;
